// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import type {Channel} from '@app/features/channel/models/Channel';
import ChannelDisplayName from '@app/features/channel/state/ChannelDisplayName';
import {compareChannelPosition} from '@app/features/messaging/utils/ChannelShared';
import {LinkChannelIcon} from '@app/features/ui/components/icons/LinkChannelIcon';
import {LockedLinkChannelIcon} from '@app/features/ui/components/icons/LockedLinkChannelIcon';
import {LockedTextChannelIcon} from '@app/features/ui/components/icons/LockedTextChannelIcon';
import {LockedVoiceChannelIcon} from '@app/features/ui/components/icons/LockedVoiceChannelIcon';
import {NoConnectChannelIcon} from '@app/features/ui/components/icons/NoConnectChannelIcon';
import {NSFWLinkChannelIcon} from '@app/features/ui/components/icons/NSFWLinkChannelIcon';
import {NSFWTextChannelIcon} from '@app/features/ui/components/icons/NSFWTextChannelIcon';
import {NSFWVoiceChannelIcon} from '@app/features/ui/components/icons/NSFWVoiceChannelIcon';
import {TextChannelIcon} from '@app/features/ui/components/icons/TextChannelIcon';
import {VoiceChannelIcon} from '@app/features/ui/components/icons/VoiceChannelIcon';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FLUXERBOT_ID} from '@app/vendor/constants/AppConstants';
import {ChannelTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {CaretDownIcon, type IconProps, NotePencilIcon} from '@phosphor-icons/react';
export function compareChannels(a: Channel, b: Channel): number {
	return compareChannelPosition(a, b);
}
function isChannelRoleRequired(channel: {
	type: number;
	guildId?: string;
	permissionOverwrites?: Readonly<Record<string, {deny: bigint}>>;
}): boolean {
	if (!channel.guildId || !channel.permissionOverwrites) return false;
	const overwrite = channel.permissionOverwrites[channel.guildId];
	if (!overwrite) return false;
	switch (channel.type) {
		case ChannelTypes.GUILD_VOICE:
			return (overwrite.deny & Permissions.CONNECT) === Permissions.CONNECT;
		case ChannelTypes.GUILD_TEXT:
		case ChannelTypes.GUILD_LINK:
			return (overwrite.deny & Permissions.VIEW_CHANNEL) === Permissions.VIEW_CHANNEL;
		default:
			return false;
	}
}
export function getIcon(
	channel: {
		type: number;
		nsfw?: boolean;
		guildId?: string;
		permissionOverwrites?: Readonly<Record<string, {deny: bigint}>>;
	},
	props: IconProps = {},
	options?: {locked?: boolean},
) {
	const roleRequired = isChannelRoleRequired(channel);
	switch (channel.type) {
		case ChannelTypes.GUILD_TEXT: {
			if (channel.nsfw) return <NSFWTextChannelIcon {...props} />;
			if (roleRequired) return <LockedTextChannelIcon {...props} />;
			return <TextChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_VOICE: {
			if (channel.nsfw) return <NSFWVoiceChannelIcon {...props} />;
			if (options?.locked) return <NoConnectChannelIcon {...props} />;
			if (roleRequired) return <LockedVoiceChannelIcon {...props} />;
			return <VoiceChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_LINK: {
			if (channel.nsfw) return <NSFWLinkChannelIcon {...props} />;
			if (roleRequired) return <LockedLinkChannelIcon {...props} />;
			return <LinkChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_CATEGORY:
			return <CaretDownIcon weight="bold" {...props} />;
		case ChannelTypes.DM_PERSONAL_NOTES:
			return <NotePencilIcon weight="bold" {...props} />;
		default:
			return <TextChannelIcon {...props} />;
	}
}
export function getName(channel: Channel) {
	switch (channel.type) {
		case ChannelTypes.GUILD_VOICE:
			return channel.nsfw ? i18n._(msg`Voice (NSFW)`) : i18n._(msg`Voice`);
		case ChannelTypes.GUILD_CATEGORY:
			return i18n._(msg`Category`);
		case ChannelTypes.GUILD_LINK:
			return channel.nsfw ? i18n._(msg`Link (NSFW)`) : i18n._(msg`Link`);
		default:
			return channel.nsfw ? i18n._(msg`Text (NSFW)`) : i18n._(msg`Text`);
	}
}
const getDirectMessageDisplayName = (channel: Channel): string => {
	if (channel.recipientIds.length === 0) {
		return i18n._(msg`Unknown User`);
	}
	const recipient = Users.getUser(channel.recipientIds[0]);
	const nickname = recipient ? NicknameUtils.getNickname(recipient) : null;
	return nickname ?? i18n._(msg`Unknown User`);
};
const getGroupDMDisplayName = (channel: Channel): string => {
	const customName = channel.name?.trim() ?? '';
	if (customName.length > 0) {
		return customName;
	}
	return ChannelDisplayName.getDisplayName(channel.id) ?? i18n._(msg`Unnamed Group`);
};
export function getDMDisplayName(channel: Channel): string {
	switch (channel.type) {
		case ChannelTypes.DM_PERSONAL_NOTES:
			return i18n._(msg`Personal Notes`);
		case ChannelTypes.DM:
			return getDirectMessageDisplayName(channel);
		case ChannelTypes.GROUP_DM:
			return getGroupDMDisplayName(channel);
		default:
			return channel.name || i18n._(msg`Unknown Channel`);
	}
}
export function isSystemDmChannel(channel: Channel): boolean {
	return channel.type === ChannelTypes.DM && channel.recipientIds.includes(FLUXERBOT_ID);
}
